/**
 * Audio procedural (Web Audio API). Arranca tras gesto del usuario.
 * Sin assets binarios; sustituible por AudioBuffer de archivos reales.
 */

/** Máximo a esperar resume(); sin gesto de usuario la promesa puede no resolverse nunca. */
export const AUDIO_RESUME_TIMEOUT_MS = 150;

export class AudioBus {
  constructor() {
    this.ctx = null;
    this.enabled = true;
    this._muted = false;
  }

  /**
   * Crea el AudioContext. Nunca bloquea el arranque de la experiencia:
   * sin transient activation, `resume()` puede quedar pending indefinidamente
   * (p. ej. deep-link `?mode=ar` con click programático).
   */
  async unlock(resumeTimeoutMs = AUDIO_RESUME_TIMEOUT_MS) {
    if (this.ctx) {
      await this._tryResume(resumeTimeoutMs);
      return;
    }
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) {
      this.enabled = false;
      return;
    }
    this.ctx = new Ctx();
    await this._tryResume(resumeTimeoutMs);
  }

  async _tryResume(timeoutMs) {
    if (!this.ctx || this.ctx.state !== 'suspended') return;
    try {
      await Promise.race([
        this.ctx.resume(),
        new Promise((resolve) => setTimeout(resolve, timeoutMs)),
      ]);
    } catch {
      // Sin gesto el browser puede rechazar; el audio se habilitará en el próximo unlock.
    }
  }

  setMuted(muted) {
    this._muted = muted;
  }

  _tone(freq, duration = 0.12, type = 'sine', gain = 0.08) {
    if (!this.ctx || this._muted || !this.enabled) return;
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume();
    }
    const t0 = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(gain, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + duration);
    osc.connect(g);
    g.connect(this.ctx.destination);
    osc.start(t0);
    osc.stop(t0 + duration);
  }

  collect() {
    this._tone(660, 0.1, 'triangle', 0.09);
    this._tone(990, 0.08, 'sine', 0.05);
  }

  start() {
    this._tone(440, 0.15, 'square', 0.06);
    this._tone(554, 0.18, 'sine', 0.05);
  }

  win() {
    [523, 659, 784, 1046].forEach((f, i) => {
      setTimeout(() => this._tone(f, 0.16, 'triangle', 0.07), i * 90);
    });
  }

  lose() {
    this._tone(220, 0.25, 'sawtooth', 0.05);
    this._tone(165, 0.3, 'sine', 0.05);
  }
}
